import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Lock, Star } from "lucide-react";

import { useWallet } from "../hooks/useWallet";
import { useCheckout } from "../contexts/CheckoutContext";
import { auditDeep, auditQuick } from "../lib/audit";
import { useActiveTab, useAuditorTabs } from "../contexts/AuditorTabsContext";
import GlassCard from "../components/GlassCard";
import GoldButton from "../components/GoldButton";
import AuditReport from "../components/AuditReport";
import BounceState from "../components/BounceState";
import AgentScanner from "../components/AgentScanner";
import CertificationOverlay from "../components/CertificationOverlay";
import WalletBadge from "../components/WalletBadge";
import WalletSyncFooter from "../components/WalletSyncFooter";
import AuditorTabsBar from "../components/AuditorTabsBar";

const FREE_DAILY_LIMIT = 3;
const VALID_TIERS = new Set(["silver", "gold", "master"]);
const DEMO_TRIGGER = "[DEMO_MODE]";
const DEMO_SIGNATURE = "DEMO-A3F2-B9C1-E7D4-8F62";

// Fixed demo payload — visually renders as a Master-tier result without
// touching real backend scoring or NXT balances. See PRD Phase 6.
function buildDemoResult(mode) {
  return {
    audit_id: `demo_${
      (typeof crypto !== "undefined" && crypto.randomUUID)
        ? crypto.randomUUID()
        : Math.random().toString(36).slice(2)
    }`,
    bounced: false,
    mode,
    nexa_score: 9.8,
    status: "Nexa Optimized",
    criteria: { clarity: 9.8, constraints: 9.7, efficiency: 9.9, resilience: 9.8 },
    stress_test: [
      "Demonstration mode active — bypasses production scoring.",
      "Real audits require the backend engine.",
      "This preview shows the visual flow only.",
    ],
    suggestions: [
      "Remove [DEMO_MODE] to run a real audit.",
      "The score shown is fixed for demo purposes.",
      "Certificate export is watermarked in metadata.",
    ],
    detected_target_model: "generic",
    hallucination_risk: "low",
    llm_provider: null,
    llm_model: null,
    elapsed_ms: 0,
    tier: "master",
    __demo_mode: true,
    __demo_signature: DEMO_SIGNATURE,
  };
}

let _demoWarnEmitted = false;
function emitDemoWarnOnce() {
  if (_demoWarnEmitted) return;
  _demoWarnEmitted = true;
  console.warn(
    "[Nexalytics] DEMO_MODE trigger active — backend response bypassed for UI preview only.",
  );
}

export default function Auditor() {
  const { t, i18n } = useTranslation();
  const {
    isPro,
    balance,
    remaining,
    refresh,
    hasDeepQuota,
    lastUpdated,
  } = useWallet();
  const { openCheckout } = useCheckout();
  const [params] = useSearchParams();
  const forceTierParam = params.get("force_tier");
  const forceTier = VALID_TIERS.has(forceTierParam) ? forceTierParam : null;

  // Phase 10 Part B: per-tab isolated state via AuditorTabsContext.
  const { activeTabId, updateTab, markCompleted } = useAuditorTabs();
  const { tab, update: updateActiveTab } = useActiveTab();

  const prompt = tab?.prompt ?? "";
  const mode = tab?.mode ?? "quick";
  const loading = tab?.loading ?? false;
  const result = tab?.result ?? null;

  const [certOpen, setCertOpen] = useState(false);
  const [certTier, setCertTier] = useState(null);
  // Prevents re-auto-opening the cert overlay on tab-switch or refresh.
  const certAutoOpenedRef = useRef(new Set());
  // Skips auto-open on first render / hydration (so a persisted result
  // does not pop the cert overlay right after page load).
  const firstResultEffectRef = useRef(true);
  const textareaRef = useRef(null);

  // Live active tab id — needed so that in-flight audits can detect if the
  // user switched away and we should surface a background completion.
  const activeTabIdRef = useRef(activeTabId);
  useEffect(() => {
    activeTabIdRef.current = activeTabId;
  }, [activeTabId]);

  const lang = i18n.resolvedLanguage || "en";
  const charCount = prompt.length;
  const tooShort = charCount > 0 && charCount < 10;

  // Hydrate re-audit prompt from history page into the ACTIVE tab if empty.
  useEffect(() => {
    try {
      const stash = sessionStorage.getItem("nxa_reaudit_prompt");
      if (stash && !prompt) {
        updateActiveTab({ prompt: stash });
        sessionStorage.removeItem("nxa_reaudit_prompt");
      }
    } catch {
      /* ignore */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTabId]);

  // Close the cert overlay whenever the user switches tabs.
  useEffect(() => {
    setCertOpen(false);
    setCertTier(null);
  }, [activeTabId]);

  // If deep gets locked while selected in this tab, fall back to quick silently.
  useEffect(() => {
    if (mode === "deep" && !hasDeepQuota) updateActiveTab({ mode: "quick" });
  }, [mode, hasDeepQuota, updateActiveTab]);

  // Auto-open cert overlay only on fresh, unseen results (per audit_id).
  useEffect(() => {
    if (firstResultEffectRef.current) {
      firstResultEffectRef.current = false;
      return;
    }
    if (!result || result.bounced) return;
    const auditId = result.audit_id;
    if (!auditId || certAutoOpenedRef.current.has(auditId)) return;
    const tier = result.__demo_mode
      ? "master"
      : forceTier || result.tier || null;
    if (tier) {
      certAutoOpenedRef.current.add(auditId);
      setCertTier(tier);
      setCertOpen(true);
    }
  }, [result, forceTier]);

  const onPickCategory = useCallback(
    (_cat, template) => {
      const cur = prompt;
      const sep = cur && !cur.endsWith("\n") ? "\n\n" : "";
      updateActiveTab({ prompt: cur + sep + template });
      setTimeout(() => {
        textareaRef.current?.focus();
        textareaRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 0);
      toast(t("bounce.templateApplied"));
    },
    [prompt, updateActiveTab, t],
  );

  const openRecharge = useCallback(() => {
    openCheckout({ kind: "recharge", packageId: "nxt_500" });
  }, [openCheckout]);

  const onClickDeepLocked = () => {
    toast(t("auditor.deep.locked_tooltip"));
    openCheckout({ kind: "recharge", packageId: "nxt_100" });
  };

  const handleErrorStatus = useCallback(
    (status, body) => {
      if (status === 429) {
        toast.error(t("auditor.errorQuotaTitle"), {
          description: t("auditor.errorQuotaBody"),
        });
        return true;
      }
      if (status === 402) {
        toast.error(t("auditor.errorNxtTitle"), {
          description: t("auditor.errorNxtBody"),
          action: {
            label: t("auditor.rechargeNxt"),
            onClick: openRecharge,
          },
        });
        return true;
      }
      if (status === 401) {
        toast.error(t("auth.errorGeneric"));
        return true;
      }
      toast.error(t("common.error"), {
        description: typeof body?.detail === "string" ? body.detail : null,
      });
      return false;
    },
    [t, openRecharge],
  );

  const onSubmit = useCallback(
    async (e) => {
      e?.preventDefault?.();
      if (loading) return;
      if (charCount === 0) return;

      // Snapshot the tab id at the moment the audit was launched. If the
      // user switches tabs while the request is in flight, we still persist
      // the result to the ORIGINATING tab and pop a background-completion
      // toast + pulse the tab pill.
      const originTabId = activeTabId;
      const originTitle = tab?.title || "";
      const originPrompt = prompt;
      const originMode = mode;

      // --- [DEMO_MODE] override ------------------------------------------
      // Substring must match verbatim, case-sensitive. Backend audit STILL
      // runs (fire-and-forget) so real telemetry/history is preserved, but
      // the UI state uses a fixed mocked result and never touches the wallet.
      const demoActive = originPrompt.includes(DEMO_TRIGGER);
      if (demoActive) {
        emitDemoWarnOnce();
        updateTab(originTabId, { loading: true, result: null });
        const fn = originMode === "deep" ? auditDeep : auditQuick;
        fn(originPrompt, lang).catch(() => {
          /* discard — demo path owns the UI */
        });
        const demoResult = buildDemoResult(originMode);
        updateTab(originTabId, { loading: false, result: demoResult });
        if (originTabId !== activeTabIdRef.current) {
          markCompleted(originTabId);
        }
        // DO NOT refresh() — balance must stay untouched by the demo flow.
        return;
      }
      // --- end demo override ---------------------------------------------

      if (tooShort) {
        toast(t("auditor.errorTooShort"));
      }
      updateTab(originTabId, { loading: true, result: null, error: null });
      const fn = originMode === "deep" ? auditDeep : auditQuick;
      const res = await fn(originPrompt, lang);

      if (!res.ok) {
        updateTab(originTabId, {
          loading: false,
          error: typeof res.body?.detail === "string" ? res.body.detail : null,
        });
        handleErrorStatus(res.status, res.body);
        return;
      }

      updateTab(originTabId, { loading: false, result: res.data });
      refresh();

      // Background completion feedback: gold NXT-style toast + pulse pill.
      if (originTabId !== activeTabIdRef.current) {
        markCompleted(originTabId);
        toast(
          t("auditor.tabs.completed", {
            title: originTitle || t("auditor.tabs.new"),
          }),
          {
            className: "nxa-toast-tab-completed",
            duration: 4500,
          },
        );
      }

      // Phase 8: notify Nexa-Core so it can show a motivational tooltip
      // when the score dropped below 8.0. Only fires for the active tab —
      // background audits shouldn't hijack the active tab's coach.
      //
      // Phase 13: ALSO dispatch 'nexacore:celebrate' on every non-bounced
      // audit so the orb can trigger a small confetti burst + speech
      // bubble when score >= 8.0. The listeners guard on score range
      // themselves (coach for <8, celebrate for >=8), so firing both is
      // safe and gives us a cleaner Auditor-side contract.
      if (
        res.data &&
        !res.data.bounced &&
        originTabId === activeTabIdRef.current
      ) {
        const score = parseFloat(res.data.nexa_score);
        try {
          window.dispatchEvent(
            new CustomEvent("nexacore:coach", { detail: { score } }),
          );
          window.dispatchEvent(
            new CustomEvent("nexacore:celebrate", { detail: { score } }),
          );
        } catch {
          /* environment without CustomEvent — ignore */
        }
      }
    },
    [
      loading,
      charCount,
      activeTabId,
      prompt,
      mode,
      tab?.title,
      lang,
      tooShort,
      t,
      handleErrorStatus,
      updateTab,
      refresh,
      markCompleted,
    ],
  );

  const canShowCertBtn = !!(
    result &&
    !result.bounced &&
    (result.__demo_mode || forceTier || result.tier)
  );

  const reopenCertificate = () => {
    const tier = result?.__demo_mode
      ? "master"
      : forceTier || result?.tier;
    if (!tier) return;
    setCertTier(tier);
    setCertOpen(true);
  };

  const modeOptions = useMemo(
    () => [
      { key: "quick", label: t("auditor.modeQuick"), hint: t("auditor.modeQuickHint") },
      {
        key: "deep",
        label: t("auditor.modeDeep"),
        hint: t("auditor.modeDeepHint"),
        locked: !hasDeepQuota,
      },
    ],
    [t, hasDeepQuota],
  );

  return (
    <section className="grid gap-4" data-testid="auditor-page">
      <AuditorTabsBar />
      <div className="grid gap-6 lg:grid-cols-[5fr_6fr]">
        {/* Left — input */}
        <GlassCard className="p-6 sm:p-7">
          <header className="mb-5 flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <h1 className="font-display text-2xl sm:text-3xl text-white tracking-tight">
                {t("auditor.headline")}
              </h1>
              <p className="mt-1 text-sm text-white/60 max-w-md">
                {t("auditor.subhead")}
              </p>
              <button
                type="button"
                onClick={() =>
                  window.dispatchEvent(new CustomEvent("nexacore:restart-tour"))
                }
                data-testid="auditor-restart-tour-btn"
                className="mt-2 text-[11px] uppercase tracking-[0.18em] text-white/45 hover:text-[#D4AF37] transition-colors"
              >
                {t("nexacore.tour.restart")}
              </button>
            </div>
            <WalletBadge />
          </header>

          <form onSubmit={onSubmit} className="grid gap-4" data-testid="auditor-form">
            <label className="grid gap-2 text-sm">
              <span className="text-white/65 font-medium">
                {t("auditor.promptLabel")}
              </span>
              <textarea
                ref={textareaRef}
                value={prompt}
                onChange={(e) => updateActiveTab({ prompt: e.target.value })}
                placeholder={t("auditor.promptPlaceholder")}
                data-testid="auditor-prompt-input"
                className="nxa-input min-h-[280px] resize-y leading-relaxed font-sans"
              />
            </label>

            <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-white/45">
              <span>{t("auditor.minHint")}</span>
              <span data-testid="auditor-charcount">
                {t("auditor.charCount", { count: charCount })}
              </span>
            </div>

            <div className="text-xs text-white/55" data-testid="auditor-quota-line">
              <span className="text-white/40">{t("auditor.remainingToday")}: </span>
              <span className="font-semibold text-white" data-testid="auditor-remaining">
                {isPro ? "∞" : `${remaining} / ${FREE_DAILY_LIMIT}`}
              </span>
              <span className="mx-2 text-white/20">·</span>
              <span className="text-white/40">{t("auditor.balance")}: </span>
              <span className="font-semibold text-[#D4AF37]" data-testid="auditor-balance">
                {balance} NXT
              </span>
            </div>

            {/* Mode toggle */}
            <fieldset className="grid gap-2">
              <legend className="text-xs uppercase tracking-[0.16em] text-white/45">
                {t("auditor.modeLabel")}
              </legend>
              <div
                className="inline-flex w-full rounded-2xl border border-white/10 bg-white/[0.03] p-1"
                data-testid="auditor-mode-toggle"
              >
                {modeOptions.map((opt) => {
                  const active = mode === opt.key;
                  if (opt.locked) {
                    return (
                      <button
                        key={opt.key}
                        type="button"
                        onClick={onClickDeepLocked}
                        aria-pressed={false}
                        title={t("auditor.deep.locked_tooltip")}
                        data-testid={`auditor-mode-${opt.key}`}
                        className="group flex-1 rounded-xl px-4 py-2.5 text-sm font-medium text-left opacity-40 cursor-not-allowed relative"
                      >
                        <div className="flex items-center gap-2">
                          <Lock className="h-3.5 w-3.5 text-[#1E40AF]" />
                          <span className="font-semibold text-white/75">
                            {opt.label}
                          </span>
                        </div>
                        <div className="text-[11px] text-white/45 mt-0.5">
                          {opt.hint}
                        </div>
                        <span
                          className="pointer-events-none absolute left-1/2 top-full mt-2 -translate-x-1/2 whitespace-nowrap rounded-md border border-white/10 bg-[#0a0b0f] px-2.5 py-1 text-[11px] text-white/80 opacity-0 shadow-lg transition-opacity group-hover:opacity-100"
                          role="tooltip"
                          data-testid="auditor-deep-locked-tooltip"
                        >
                          {t("auditor.deep.locked_tooltip")}
                        </span>
                      </button>
                    );
                  }
                  return (
                    <button
                      key={opt.key}
                      type="button"
                      onClick={() => updateActiveTab({ mode: opt.key })}
                      aria-pressed={active}
                      data-testid={`auditor-mode-${opt.key}`}
                      className={
                        "flex-1 rounded-xl px-4 py-2.5 text-sm font-medium transition-colors text-left " +
                        (active
                          ? "bg-gradient-to-b from-[#1B1D27] to-[#0c0d12] text-white border border-white/10 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]"
                          : "text-white/55 hover:text-white/80")
                      }
                    >
                      <div className="flex items-center gap-2">
                        <span
                          className={
                            "inline-block h-1.5 w-1.5 rounded-full " +
                            (opt.key === "deep" ? "bg-[#1E40AF]" : "bg-[#D4AF37]")
                          }
                        />
                        <span className="font-semibold">{opt.label}</span>
                      </div>
                      <div className="text-[11px] text-white/45 mt-0.5">{opt.hint}</div>
                    </button>
                  );
                })}
              </div>
              {!hasDeepQuota && (
                <button
                  type="button"
                  onClick={openRecharge}
                  className="self-start text-xs text-[#D4AF37] hover:text-[#F4D77A] transition-colors"
                  data-testid="auditor-deep-unlock-cta"
                >
                  {t("auditor.deep.unlock_cta")} →
                </button>
              )}
            </fieldset>

            <GoldButton
              type="submit"
              disabled={loading || charCount === 0}
              className="w-full justify-center mt-1"
              data-testid="auditor-run-btn"
            >
              {loading ? t("auditor.running") : t("auditor.run")}
            </GoldButton>
          </form>

          <WalletSyncFooter lastUpdated={lastUpdated} keyPath="auditor.syncLine" />
        </GlassCard>

        {/* Right — report */}
        <GlassCard className="p-6 sm:p-7 min-h-[480px]">
          {loading ? (
            <AgentScanner />
          ) : !result ? (
            <EmptyState />
          ) : result.bounced ? (
            <BounceState
              message={result.message_localized}
              onPickCategory={onPickCategory}
            />
          ) : (
            <AuditReport
              result={result}
              onShowCertificate={canShowCertBtn ? reopenCertificate : null}
            />
          )}
        </GlassCard>
      </div>

      <CertificationOverlay
        open={certOpen}
        tier={certTier}
        audit={result}
        onClose={() => setCertOpen(false)}
      />
    </section>
  );
}

function EmptyState() {
  const { t } = useTranslation();
  return (
    <div
      className="flex h-full min-h-[400px] flex-col items-center justify-center text-center gap-4"
      data-testid="auditor-empty"
    >
      <span className="inline-flex h-14 w-14 items-center justify-center rounded-full border border-[#D4AF37]/30 bg-[#D4AF37]/[0.06]">
        <Star className="h-6 w-6 text-[#D4AF37]" strokeWidth={1.5} />
      </span>
      <p className="max-w-sm text-sm text-white/55 leading-relaxed">
        {t("auditor.empty")}
      </p>
    </div>
  );
}
